import React, { useState, useMemo, useEffect, useRef } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { format, parseISO } from "date-fns";
import { Form, Col, Row, Dropdown, Button } from "react-bootstrap";
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, getFilteredRowModel, flexRender } from "@tanstack/react-table";
import { FaCog, FaChevronDown } from "react-icons/fa";
import DateRangeFilter from "../components/DateRangeFilter";
import SelectFilter from "../components/SelectFilter";
import { debounce } from "lodash";
import useFetchData from "../components/useFetchDatav2";
import { startOfMonth, endOfMonth } from "date-fns";
import { useToast } from "../Context/useToast";

import { DOCUMENT_TYPES } from "../constants/documentTypes";
import { DOCUMENT_STATUS } from "../constants/documentStatus";
import prettyBytes from "pretty-bytes";
import InvoiceModal from "../components/InvoiceModal";
import PdfModal from "../components/PdfModal";

const styles = `
  #dropdown-table-columns::after {
    display: none !important;
  }
  .dropdown-table-columns-width {
    width: 200px;
  }
  .hidden-input {
    opacity: 0;
  }
`;

const TableSettingDropdown = ({ showDropdown, handleToggleDropdown, tempPagination, handlePageSizeChange, table, tempColumnVisibility, handleColumnVisibilityChange, handleApplyChanges, handleCancelChanges }) => {
   return (
      <Dropdown show={showDropdown} onToggle={handleToggleDropdown}>
         <Dropdown.Toggle as="span" variant="link" id="dropdown-table-columns" style={{ cursor: "pointer" }}>
            <FaCog />
         </Dropdown.Toggle>
         <Dropdown.Menu className="dropdown-table-columns-width" onClick={(e) => e.stopPropagation()}>
            <Dropdown.Header>Rows</Dropdown.Header>
            <div className="dropdown-item">
               <Form.Check
                  type="checkbox"
                  label="10"
                  checked={tempPagination.pageSize === 10}
                  onChange={(e) => {
                     e.stopPropagation();
                     handlePageSizeChange(10);
                  }}
               />
            </div>
            <div className="dropdown-item">
               <Form.Check
                  type="checkbox"
                  label="20"
                  checked={tempPagination.pageSize === 20}
                  onChange={(e) => {
                     e.stopPropagation();
                     handlePageSizeChange(20);
                  }}
               />
            </div>
            <div className="dropdown-item">
               <Form.Check
                  type="checkbox"
                  label="50"
                  checked={tempPagination.pageSize === 50}
                  onChange={(e) => {
                     e.stopPropagation();
                     handlePageSizeChange(50);
                  }}
               />
            </div>
            <Dropdown.Divider />
            <Dropdown.Header>View Columns</Dropdown.Header>
            {table
               .getAllColumns()
               .filter((column) => !column.columnDef.excludeFromDropdown)
               .map((column) => (
                  <div className="dropdown-item" key={column.id} as="label">
                     <div className="form-check">
                        <input
                           type="checkbox"
                           className="form-check-input"
                           checked={tempColumnVisibility?.[column.id] ?? true}
                           onChange={(e) => {
                              e.stopPropagation();
                              handleColumnVisibilityChange(column.id);
                           }}
                           id={`column-visibility-${column.id}`}
                        />
                        <label className="form-check-label" htmlFor={`column-visibility-${column.id}`}>
                           {column.id}
                        </label>
                     </div>
                  </div>
               ))}
            <Dropdown.Divider />
            <div className="d-flex justify-content-center p-2">
               <Button variant="secondary" size="sm" onClick={handleCancelChanges}>
                  Cancel
               </Button>
               <Button variant="primary" size="sm" onClick={handleApplyChanges} className="ms-2">
                  Apply
               </Button>
            </div>
         </Dropdown.Menu>
      </Dropdown>
   );
};
export default function Invoices() {
   const defaultDateRange = {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      key: "selection",
   };
   const [isLoading, setIsLoading] = useState(true);
   const [showInvoiceModal, setShowInvoiceModal] = useState(false);
   const [error, setError] = useState(null);
   const [invId, setInvId] = useState(null);
   const [invName, setInvName] = useState(null);
   const [imageData, setImageData] = useState(null);
   const { fetchData } = useFetchData();
   const [docObj, setDocObj] = useState(null);
   const { triggerToast } = useToast();
   const now = new Date();
   const startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1, 0, 0, 0);
   const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

   const [invoices, setInvoices] = useState([]);
   const [columnFilters, setColumnFilters] = useState({
      inv_date: defaultDateRange,
   });
   const getDocumentTypeLabel = (value) => {
      const type = DOCUMENT_TYPES.find((docType) => docType.value === value.toString());
      return type ? type.label : "Unknown";
   };
   const getDocumentStatusLabel = (value) => {
      const status = DOCUMENT_STATUS.find((docStatus) => docStatus.value === value.toString());
      return status ? status.label : "Unknown";
   };
   const columns = useMemo(
      () => [
         {
            accessorKey: "esh_id",
            header: "esh_id",
            enableHiding: false, // Prevent visibility toggling
            enableColumnFilter: false, // Prevent filtering
            isVisible: false, // Always hidden
            excludeFromDropdown: true,
         },
         {
            accessorKey: "sm_name",
            accessorId: "sm_name",
            header: () => <span>Supplier</span>,
            enableColumnFilter: true,
            manualFiltering: true,
            isVisible: true,
         },
         {
            accessorKey: "esh_invoice_id",
            accessorId: "esh_invoice_id",
            header: () => <span>Invoice Number</span>,
            enableColumnFilter: true,
            manualFiltering: true,
            isVisible: true,
         },
         {
            accessorKey: "inv_date",
            header: () => <span>Invoice Date</span>,
            Filter: ({ column }) => <DateRangeFilter column={{ ...column, value: columnFilters[column.id] || "" }} handleInputFilterChangeNoDebounce={handleInputFilterChangeNoDebounce} />,
            enableColumnFilter: true,
            isVisible: true,
            cell: ({ getValue }) => {
               const dat = getValue();
               try {
                  if (!dat) {
                     return "N/A";
                  }
                  const parsedDate = parseISO(dat);
                  return format(parsedDate, "yyyy-MM-dd");
               } catch (error) {
                  return "Invalid date";
               }
            },
         },
         {
            accessorKey: "inv_po_no",
            accessorId: "inv_po_no",
            header: () => <span>P.O. No.</span>,
            enableColumnFilter: true,
            manualFiltering: true,
            isVisible: false,
         },
         {
            accessorKey: "esh_user_name",
            header: () => <span>Posted By</span>,
            enableColumnFilter: true,
            isVisible: true,
         },
         {
            accessorKey: "esh_total_subtotal",
            header: () => <span>Amount</span>,
            enableColumnFilter: false,
            isVisible: true,
            cell: ({ getValue }) => {
               const amt = getValue();
               if (isNaN(amt)) return "-";
               return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 2,
               }).format(amt);
            },
         },
         {
            accessorKey: "action",
            header: () => <span>Action</span>,
            enableHiding: true,
            enableResizing: false,
            excludeFromDropdown: true,
            minSize: 125,
            maxSize: 125,
            width: 125,
            cell: (info) => (
               <div
                  style={{
                     whiteSpace: "nowrap",
                     overflow: "hidden",
                     textOverflow: "ellipsis",
                  }}>
                  {info.getValue()}
               </div>
            ),
         },
      ],
      [columnFilters]
   );
   const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 10,
      totalPages: 0,
   });
   const handleClearFilters = () => {
      setColumnFilters({ sm_name: "", esh_invoice_id: "", inv_date: defaultDateRange, inv_po_no: "", esh_user_name: "" });
      setPagination((prev) => ({
         ...prev,
         pageIndex: 0, // Reset to first page
      }));
   };

   const nextPage = () => {
      setPagination((prev) => {
         const newPageIndex = Math.min(prev.pageIndex + 1, prev.totalPages - 1);
         const newPagination = { ...prev, pageIndex: newPageIndex };

         const formData = fetchFilters(newPagination, columnFilters);
         fetchInvoices(formData);

         return newPagination;
      });
   };
   const previousPage = () => {
      setPagination((prev) => {
         const newPageIndex = Math.max(prev.pageIndex - 1, 0);
         const newPagination = { ...prev, pageIndex: newPageIndex };

         const formData = fetchFilters(newPagination, columnFilters);
         fetchInvoices(formData);

         return newPagination;
      });
   };
   const [columnVisibility, setColumnVisibility] = useState(() => {
      return columns
         ? columns.reduce((acc, column) => {
              acc[column.accessorKey] = column.isVisible !== false; // Start with all columns visible
              return acc;
           }, {})
         : {};
   });
   const [showDropdown, setShowDropdown] = useState(false);
   const [tempPagination, setTempPagination] = useState(pagination);
   const [tempColumnVisibility, setTempColumnVisibility] = useState(columnVisibility);
   // Log the columnVisibility to check its value
   useEffect(() => {
      console.log("Column Visibility:", tempColumnVisibility);
   }, [tempColumnVisibility]); // This will log whenever columnVisibility changes
   const handleApplyChanges = (size) => {
      // Apply temporary state to actual table settings
      setPagination(tempPagination);
      setColumnVisibility(tempColumnVisibility);
      setShowDropdown(false); // Close dropdown on apply
   };
   const handleCancelChanges = () => {
      // Reset temporary state to match actual table settings
      setPagination(tempPagination);
      setTempColumnVisibility(columnVisibility);
      setShowDropdown(false); // Close dropdown on apply
   };
   const table = useReactTable({
      data: invoices,
      columns,
      tableLayout: "fixed",
      pageCount: pagination.totalPages,
      manualPagination: true,
      manualFiltering: true,
      onColumnFiltersChange: setColumnFilters,
      state: {
         columnVisibility,
         columnFilters,
      },
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });
   const handleToggleDropdown = (isOpen) => {
      setShowDropdown(isOpen);
      if (isOpen) {
         setTempPagination(pagination);
         setTempColumnVisibility(columnVisibility);
      }
   };
   const handlePageSizeChange = (size) => {
      setTempPagination({
         ...tempPagination,
         pageSize: size,
      });
   };
   const handleColumnVisibilityChange = (columnId) => {
      setTempColumnVisibility((prev) => ({
         ...prev,
         [columnId]: !prev[columnId],
      }));
   };
   const updateTotalPages = (totalPages) => {
      setPagination((prev) => {
         if (prev.totalPages === totalPages) {
            return prev; // avoid re-render loop
         }
         return { ...prev, totalPages };
      });
   };
   const fetchInvoices = async (filters) => {
      const url = "/getInvoices.sz";
      const method = "POST";
      try {
         const { responseData, fetchLoading, fetchError } = await fetchData(url, method, filters, "form-data");
         console.log("RESPONSEDATA:", responseData);
         if (responseData.st === 1) {
            if (Array.isArray(responseData.payload)) {
               setInvoices(responseData.payload);
               console.log("Previous Pagination State:", pagination);
               console.log("Updating Total Pages to:", responseData.total_pages);
               updateTotalPages(responseData.total_pages);
            } else {
               console.error("Invalid data structure, payload is not an array");
            }
         } else {
            if (responseData.m_en) {
               setError(responseData.m_en);
            } else {
               setError("We seem to have an issue authenticating, please try again later.");
            }
         }
      } catch (error) {
         console.error("Error fetching data:", error);
      }
   };
   const fetchFilters = (pagination, columnFilters) => {
      const formData = new FormData();
      formData.append("_page_index", pagination.pageIndex ?? 0);
      formData.append("_page_size", pagination.pageSize ?? 10);
      if (columnFilters.sm_name) {
         formData.append("_sm_name", columnFilters.sm_name);
      }
      if (columnFilters.esh_invoice_id) {
         formData.append("_esh_invoice_id", columnFilters.esh_invoice_id);
      }
      if (columnFilters.esh_user_name) {
         formData.append("_esh_user_name", columnFilters.esh_user_name);
      }
      if (columnFilters.inv_po_no) {
         formData.append("_inv_po_no", columnFilters.inv_po_no);
      }
      if (columnFilters.inv_date?.startDate) {
         formData.append("_inv_startdate", format(columnFilters.inv_date.startDate, "yyyy-MM-dd"));
      }
      if (columnFilters.inv_date?.endDate) {
         formData.append("_inv_enddate", format(columnFilters.inv_date.endDate, "yyyy-MM-dd"));
      }
      return formData;
   };
   useEffect(() => {
      console.log("COLUMNFILTERS:", columnFilters);
      const formData = fetchFilters(pagination, columnFilters);
      fetchInvoices(formData);
   }, [pagination, columnFilters]);

   const debouncedFilterChange = useMemo(
      () =>
         debounce((columnId, value) => {
            setColumnFilters((prev) => ({
               ...prev,
               [columnId]: value,
            }));

            // Add your server call here
            console.log(`Filtering for column ${columnId} with value: ${value}`);
         }, 500), // Adjust delay as needed
      []
   );

   const handleInputFilterChangeNoDebounce = (columnId, value) => {
      setColumnFilters((prev) => {
         console.log("Previous Filters:", prev); // Log previous filters (prev state)
         console.log("Column ID:", columnId); // Log the columnId being passed
         console.log("Value:", value); // Log the value being passed
         const next = { ...prev, [columnId]: value };
         return next;
      });

      setPagination((prev) => ({
         ...prev,
         pageIndex: 0, // Reset to first page when filter changes
      }));
   };

   const handleInputFilterChange = (columnId, value) => {
      debouncedFilterChange(columnId, value);
   };
   // Cleanup debounce on component unmount
   useEffect(() => {
      return () => {
         debouncedFilterChange.cancel();
      };
   }, [debouncedFilterChange]);

   // this listens for the "View" button
   const handleViewPdf = async ({ row }) => {
      if (row.original) {
         const docObj = {
            id: row.original.doc_id,
            name: row.original.doc_name,
            uploadOn: row.original.doc_upload_date,
            uploadBy: row.original.doc_upload_by,
         };
         setDocObj(docObj);
         await fetchPdfContent({ docObj });
         setShowInvoiceModal(true);
      }
   };

   const fetchPdfContent = async ({ docObj }) => {
      if (!docObj) return;
      setIsLoading(true);

      const url = "/getfile.sz";
      const method = "POST";
      const formData = new FormData();
      formData.append("_doc_id", docObj.id);
      try {
         const { responseData, fetchError } = await fetchData(url, method, formData, "form-data", "blob");
         if (fetchError) {
            triggerToast("error", "Failed to load PDF. Please try again.", true, "Error", false, 0);
            return;
         }
         const base64Data = await convertBlobToBase64(responseData);
         setImageData(base64Data);
      } catch (error) {
         triggerToast("error", "Network issue, please try again.", true, "Network Issue", false, 0);
      } finally {
         setIsLoading(false);
      }
   };

   // Helper function to convert Blob to Base64
   const convertBlobToBase64 = (blob) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.readAsDataURL(blob);
         reader.onloadend = () => resolve(reader.result.split(",")[1]);
         reader.onerror = reject;
      });
   };

   const handleRowClick = (inv) => {
      setInvId(inv.esh_id);
      setInvName(inv.esh_parsed_invoice_id);
      setShowInvoiceModal(true);
   };
   const handleInvoiceModalClose = () => {
      setTimeout(() => {
         setShowInvoiceModal(null);
      }, 300);
   };
   return (
      <>
         <style>{styles}</style>
         <Header />
         <div className="main main-app p-3 p-lg-4 p-xxl-5">
            <Row className="g-3 g-lg-4">
               <Col>
                  <h2 className="main-title fs-4 mb-1">Invoices</h2>
                  <p className="text-secondary mb-4"></p>
               </Col>
            </Row>
            <Row className="pt-4 pb-2">
               <Col>
                  <button type="button" className="btn btn-outline-secondary rounded-pill pt-1 pb-1">
                     Clear Filters
                  </button>
               </Col>
            </Row>
            <Row className="g-3 g-lg-4">
               <Col>
                  <div className="position-relative d-flex mb-2">
                     <table className="table">
                        <thead>
                           {table.getHeaderGroups().map((headerGroup) => (
                              <tr key={headerGroup.id}>
                                 {headerGroup.headers.map((header) => {
                                    if (columnVisibility[header.id] === false) {
                                       return null;
                                    }
                                    if (header.id.toLowerCase() === "inv_date" || header.id.toLowerCase() === "doc_type" || header.id.toLowerCase() === "doc_status") {
                                       return (
                                          <th key={header.id}>
                                             {flexRender(header.column.columnDef.header, header.getContext())}
                                             {header.column.getCanFilter() && flexRender(header.column.columnDef.Filter, header.getContext())}
                                          </th>
                                       );
                                    } else if (header.id.toLowerCase() === "action") {
                                       return (
                                          <th
                                             key={header.id}
                                             style={{
                                                maxWidth: header.column.columnDef.maxSize || "auto",
                                                minWidth: header.column.columnDef.minSize || "auto",
                                                width: header.column.columnDef.size || "auto",
                                             }}>
                                             <div className="d-flex flex-column align-items-end">
                                                <div className="d-flex align-items-center">
                                                   <div className="me-4">{flexRender(header.column.columnDef.header, header.getContext())}</div>
                                                   <span>
                                                      <TableSettingDropdown showDropdown={showDropdown} handleToggleDropdown={handleToggleDropdown} tempPagination={tempPagination} handlePageSizeChange={handlePageSizeChange} table={table} tempColumnVisibility={tempColumnVisibility} handleColumnVisibilityChange={handleColumnVisibilityChange} handleApplyChanges={handleApplyChanges} handleCancelChanges={handleCancelChanges} />
                                                   </span>
                                                </div>
                                                <div className="w-100">
                                                   <input className="form-control mt-2 hidden-input" type="text" />
                                                </div>
                                             </div>
                                          </th>
                                       );
                                    } else {
                                       return (
                                          //<th key={header.id} className={header.column.columnDef.enableColumnFilter ? "" : "d-flex justify-content-start align-items-end w-100 h-100 border-bottom-1"}>
                                          <th key={header.id}>
                                             {flexRender(header.column.columnDef.header, header.getContext())}
                                             {header.column.columnDef.enableColumnFilter ? <input className="form-control mt-2" type="text" placeholder={`Search ${header.id}`} onChange={(e) => handleInputFilterChange(header.column.id, e.target.value)} /> : <input className="form-control mt-2 hidden-input" type="text" />}
                                          </th>
                                       );
                                    }
                                 })}
                              </tr>
                           ))}
                        </thead>
                        <tbody>
                           {invoices?.length === 0 ? (
                              <tr key="tr-1">
                                 <td colSpan={columns.length}>No Invoices found...</td>
                              </tr>
                           ) : (
                              table.getRowModel().rows.map((row) => (
                                 <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                       if (cell.column.id.toLowerCase() === "action") {
                                          return (
                                             <td
                                                key={cell.id}
                                                style={{
                                                   maxWidth: cell.column.columnDef.maxSize || "auto",
                                                   minWidth: cell.column.columnDef.minSize || "auto",
                                                   width: cell.column.columnDef.size || "auto",
                                                }}>
                                                <div
                                                   style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "flex-end",
                                                      gap: "8px", // Spacing between elements
                                                   }}>
                                                   {row.original.doc_id && (
                                                      <a href="#" onClick={() => handleViewPdf({ row })}>
                                                         View Invoice
                                                      </a>
                                                   )}

                                                   {/* Disable for now <div
                                                      style={{
                                                         borderLeft: "1px solid #ccc",
                                                         height: "16px",
                                                      }}></div>
                                                   <div className="dropdown">
                                                      <button
                                                         className="btn btn-link"
                                                         type="button"
                                                         id={`dropdown-${row.id}`}
                                                         data-bs-toggle="dropdown"
                                                         aria-expanded="false"
                                                         style={{ padding: 0, border: "none", cursor: "pointer" }}>
                                                         <FaChevronDown />
                                                      </button>
                                                      <ul
                                                         className="dropdown-menu"
                                                         aria-labelledby={`dropdown-${row.id}`}>
                                                         <li>
                                                            <button
                                                               className="dropdown-item"
                                                               onClick={() => handleRowClick({ docId: row.original.doc_id, docName: row.original.doc_name })}>
                                                               View
                                                            </button>
                                                         </li>
                                                         <li>
                                                            <button
                                                               className="dropdown-item"
                                                               onClick={() => console.log(`Delete row ${row.id}`)}>
                                                               Delete
                                                            </button>
                                                         </li>
                                                      </ul>
                                                   </div> */}
                                                </div>
                                             </td>
                                          );
                                       } else {
                                          return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                                       }
                                    })}
                                 </tr>
                              ))
                           )}
                        </tbody>
                     </table>
                  </div>
               </Col>
            </Row>
            <Row className="g-3 g-lg-4">
               <Col>
                  {invoices?.length === 0 ? null : (
                     <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex justify-content-center flex-grow-1">
                           <span>
                              Page <strong>{pagination.pageIndex + 1 || 1}</strong> of {pagination.totalPages || 1}
                           </span>
                        </div>

                        <div className="d-flex justify-content-end">
                           <button className="btn btn-primary me-2" onClick={previousPage} disabled={pagination.pageIndex === 0}>
                              Previous
                           </button>
                           <button className="btn btn-primary" onClick={nextPage} disabled={pagination.pageIndex >= pagination.totalPages - 1}>
                              Next
                           </button>
                        </div>
                     </div>
                  )}
               </Col>
            </Row>
            {showInvoiceModal && <PdfModal imageData={imageData} propScale={1.0} docObj={docObj} onCloseModal={handleInvoiceModalClose} />}
            <Footer />
         </div>
      </>
   );
}
